import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import ProtectedRoute from './router/ProtectedRoute';
import RoleRoute from './router/RoleRoute';
import Spinner from './components/ui/Spinner';
import AppLayout from './components/AppLayout';
import LoginPage from './features/auth/LoginPage';
import RegisterPage from './features/auth/RegisterPage';
import AuthCallbackPage from './features/auth/AuthCallbackPage';
import WelcomePage from './features/auth/WelcomePage';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import ResetPasswordPage from './features/auth/ResetPasswordPage';
import VerifyEmailPage from './features/auth/VerifyEmailPage';
import SessionsPage from './features/auth/SessionsPage';
import ProfilePage from './features/auth/ProfilePage';
import FilesPage from './features/files/FilesPage';
import FileViewerPage from './features/files/FileViewerPage';
import EditorPage from './features/editor/EditorPage';
import ComposeMessagePage from './features/secrets/ComposeMessagePage';
import SentMessagesPage from './features/secrets/SentMessagesPage';
import PublicMessagePage from './features/secrets/PublicMessagePage';
import FileRequestsPage from './features/file-request/FileRequestsPage';
import PublicFileRequestPage from './features/file-request/PublicFileRequestPage';
import TrashPage from './features/trash/TrashPage';
import SharesPage from './features/share/SharesPage';
import MentionsPage from './features/comments/MentionsPage';
import NotificationsPage from './features/notifications/NotificationsPage';
import MembersPage from './features/tenancy/MembersPage';
import JoinInvitePage from './features/tenancy/JoinInvitePage';
import PublicSharePage from './features/share/PublicSharePage';
import InvitationPreviewPage from './features/share/InvitationPreviewPage';
import SharedFileViewerPage from './features/files/SharedFileViewerPage';
import SharedFolderViewerPage from './features/files/SharedFolderViewerPage';
import SharedWithMePage from './features/files/SharedWithMePage';
import SignaturesPage from './features/signatures/SignaturesPage';
import SignatureDetailPage from './features/signatures/SignatureDetailPage';
import DocumentWizardPage from './features/signatures/DocumentWizardPage';
import PublicSignerPage from './features/signatures/PublicSignerPage';
import VerifyDocumentPage from './features/signatures/VerifyDocumentPage';
import WaiverSignPage from './features/signatures/WaiverSignPage';
import WaiverCopyPage from './features/signatures/WaiverCopyPage';
import FormsPage from './features/forms/FormsPage';
import FormBuilderPage from './features/forms/FormBuilderPage';
import FormResponsesPage from './features/forms/FormResponsesPage';
import PublicFormPage from './features/forms/PublicFormPage';
import LandingPage from './pages/LandingPage';
import SecurityPage from './pages/SecurityPage';
import NotFound from './pages/NotFound';
import { useAuthStore } from './store/authStore';
import { useThemeSync } from './hooks/useThemeSync';
import { useScrollToHash } from './hooks/useScrollToHash';

// Marketing + admin chunks load lazily so the core app bundle is unchanged.
const PricingPage = lazy(() => import('./features/pricing/PricingPage'));
const BlogListPage = lazy(() => import('./features/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('./features/blog/BlogPostPage'));
const CorePage = lazy(() => import('./pages/products/CorePage'));
const ConversationsPage = lazy(() => import('./pages/products/ConversationsPage'));
const SecureDropPage = lazy(() => import('./pages/products/SecureDropPage'));
const SignPage = lazy(() => import('./pages/products/SignPage'));
const FlowPage = lazy(() => import('./pages/products/FlowPage'));
const AdminRoutes = lazy(() => import('./features/admin/AdminRoutes'));
const AdminLoginPage = lazy(() => import('./features/admin/AdminLoginPage'));
const BlogEditorRoutes = lazy(() => import('./features/blog-editor/BlogEditorRoutes'));
const BillingSetupPage = lazy(() => import('./features/billing/BillingSetupPage'));
const ManageSubscriptionPage = lazy(() => import('./features/billing/ManageSubscriptionPage'));
const ApiDocsPage = lazy(() => import('./features/api-docs/ApiDocsPage'));

function PublicOnly({ children }) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => Boolean(s.accessToken));
  const portalBound = useAuthStore((s) => s.user?.role === 'super_admin' && !s.impersonation);
  if (!isAuthenticated) return children;
  // On the tick login succeeds, this reactive redirect can win a race against
  // LoginPage's own imperative `navigate(from)` call — so it must agree on the
  // same destination (the deep link the user was on before being sent to
  // /login), not just default to /files, or that deep link silently drops.
  const from = location.state?.from?.pathname;
  return <Navigate to={portalBound ? '/admin' : from || '/files'} replace />;
}

function LazyFallback() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Spinner />
    </div>
  );
}

export default function App() {
  useThemeSync();
  useScrollToHash();
  return (
    <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/security" element={<SecurityPage />} />

        {/* Public marketing pages (light theme, DB-driven) */}
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Module marketing pages — one per MyDocUploads product-family module */}
        <Route path="/products/core" element={<CorePage />} />
        <Route path="/products/conversations" element={<ConversationsPage />} />
        <Route path="/products/secure-drop" element={<SecureDropPage />} />
        <Route path="/products/sign" element={<SignPage />} />
        <Route path="/products/flow" element={<FlowPage />} />

        {/* Super-admin portal (own login page; static segment outranks the wildcard) */}
        <Route
          path="/admin/login"
          element={
            <PublicOnly>
              <AdminLoginPage />
            </PublicOnly>
          }
        />
        <Route
          path="/admin/*"
          element={
            <RoleRoute>
              <AdminRoutes />
            </RoleRoute>
          }
        />

        {/* Blog Editor workspace (authed; the API checks Blog Editor access on every call) */}
        <Route
          path="/blog-editor/*"
          element={
            <ProtectedRoute>
              <BlogEditorRoutes />
            </ProtectedRoute>
          }
        />

        {/* Signup billing step (authed, standalone — no app chrome) */}
        <Route
          path="/billing/setup"
          element={
            <ProtectedRoute>
              <BillingSetupPage />
            </ProtectedRoute>
          }
        />

        {/* Public auth pages */}
        <Route
          path="/login"
          element={
            <PublicOnly>
              <LoginPage />
            </PublicOnly>
          }
        />
        <Route
          path="/register"
          element={
            <PublicOnly>
              <RegisterPage />
            </PublicOnly>
          }
        />

        {/* OAuth landing — must render even mid-session, so no PublicOnly */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* First-Google-sign-in onboarding (authed, standalone — no app chrome) */}
        <Route
          path="/welcome"
          element={
            <ProtectedRoute>
              <WelcomePage />
            </ProtectedRoute>
          }
        />

        {/* Public account flows (reached via emailed links) */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Emailed workspace-invitation landing (bounces through /login if needed) */}
        <Route path="/join/:token" element={<JoinInvitePage />} />

        {/* Public share viewer */}
        <Route path="/s/:token" element={<PublicSharePage />} />
        <Route path="/drive/s/:token" element={<PublicSharePage />} />

        {/* Public email-invite preview (no login required) */}
        <Route path="/invite/:token" element={<InvitationPreviewPage />} />
        {/* Public encrypted-message + file-request pages (capability links — no auth) */}
        <Route path="/m/:token" element={<PublicMessagePage />} />
        <Route path="/r/:token" element={<PublicFileRequestPage />} />

        {/* Public signing (capability links — no auth) */}
        <Route path="/sign/:token" element={<PublicSignerPage />} />
        <Route path="/w/copy/:downloadToken" element={<WaiverCopyPage />} />
        <Route path="/verify" element={<VerifyDocumentPage />} />
        <Route path="/w/:slug" element={<WaiverSignPage />} />
        <Route path="/form/:id" element={<PublicFormPage />} />

        {/* Full-screen API reference (authed, no app chrome; opened in a new tab) */}
        <Route
          path="/api-docs"
          element={
            <ProtectedRoute>
              <ApiDocsPage />
            </ProtectedRoute>
          }
        />

        {/* Full-screen ONLYOFFICE editor (authed, no app chrome) */}
        <Route
          path="/files/:fileId/edit"
          element={
            <ProtectedRoute>
              <EditorPage />
            </ProtectedRoute>
          }
        />

        {/* Full-screen file viewer (authed, no app chrome) */}
        <Route
          path="/files/:fileId"
          element={
            <ProtectedRoute>
              <FileViewerPage />
            </ProtectedRoute>
          }
        />

        {/* Full-screen viewer for files/folders shared with me by email (grant-based, non-member) */}
        <Route
          path="/shared/:fileId"
          element={
            <ProtectedRoute>
              <SharedFileViewerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shared-folder/:folderId"
          element={
            <ProtectedRoute>
              <SharedFolderViewerPage />
            </ProtectedRoute>
          }
        />

        {/* Authenticated app shell */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/files" element={<FilesPage />} />
          <Route path="/folder/:folderId" element={<FilesPage />} />
          <Route path="/trash" element={<TrashPage />} />
          <Route path="/shares" element={<SharesPage />} />
          <Route path="/shared-with-me" element={<SharedWithMePage />} />
          <Route path="/messages" element={<ComposeMessagePage />} />
          <Route path="/messages/sent" element={<SentMessagesPage />} />
          <Route path="/file-requests" element={<FileRequestsPage />} />
          <Route path="/mentions" element={<MentionsPage />} />
          <Route path="/signatures" element={<SignaturesPage />} />
          <Route path="/signatures/new" element={<DocumentWizardPage />} />
          <Route path="/signatures/:id/edit" element={<DocumentWizardPage />} />
          <Route path="/signatures/:id" element={<SignatureDetailPage />} />
          <Route path="/forms" element={<FormsPage />} />
          <Route path="/forms/new" element={<FormBuilderPage />} />
          <Route path="/forms/:id/edit" element={<FormBuilderPage />} />
          <Route path="/forms/:id/responses" element={<FormResponsesPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/sessions" element={<SessionsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/billing" element={<ManageSubscriptionPage />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
